/**
 * FHIR constants for Transfer Requests.
 *
 * Mirrors `docs/prd/Tasks/PRD_TRANSFER_REQUESTS_FHIR_MAPPING.md`.
 * Transfer reason codes, display labels, and reusable query helpers.
 */

import { TRANSFER_REASON_SYSTEM, TEMPLATE_CATEGORY_SYSTEM } from '@unify/fhir-model/constants';
import type { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';

export { TRANSFER_REASON_SYSTEM };

// ---------------------------------------------------------------------------
// Transfer reason codes (PRD CodeSystem/transfer-reason)
// ---------------------------------------------------------------------------

export const TRANSFER_REASON_CODES = [
  'schedule_conflict',
  'request_for_in-person',
  'interested_in_new_therapist',
  'change_of_insurance',
  'different_state',
  'interested_in_weekend_availability',
  'interested_in_evening_availability',
  'interested_in_specific_modality',
  'other',
  'provider_termination',
] as const;

export type TransferReasonCode = (typeof TRANSFER_REASON_CODES)[number];

export const TRANSFER_REASON_DISPLAY: Record<TransferReasonCode, string> = {
  schedule_conflict: 'Scheduling Conflict',
  'request_for_in-person': 'Request for In-Person',
  interested_in_new_therapist: 'Interested in New Therapist',
  change_of_insurance: 'Change of Insurance',
  different_state: 'Different State',
  interested_in_weekend_availability: 'Interested in Weekend Availability',
  interested_in_evening_availability: 'Interested in Evening Availability',
  interested_in_specific_modality: 'Interested in Specific Modality',
  other: 'Other',
  provider_termination: 'Provider no longer with practice',
};

export function isTransferReasonCode(v: unknown): v is TransferReasonCode {
  return (
    typeof v === 'string' &&
    (TRANSFER_REASON_CODES as readonly string[]).includes(v)
  );
}

/** MultiSelect-compatible options for the TransferModal. */
export const TRANSFER_REASON_OPTIONS: { value: TransferReasonCode; label: string }[] =
  TRANSFER_REASON_CODES.map((code) => ({
    value: code,
    label: TRANSFER_REASON_DISPLAY[code],
  }));

// ---------------------------------------------------------------------------
// Transfer template category
// ---------------------------------------------------------------------------

export const TRANSFER_TEMPLATE_CATEGORY = 'transfers' as const;
export const TRANSFER_TEMPLATE_CATEGORY_TAG = `${TEMPLATE_CATEGORY_SYSTEM}|${TRANSFER_TEMPLATE_CATEGORY}`;

function hasTemplateCategoryTag(
  resource: { meta?: { tag?: { system?: string; code?: string }[] } },
  category: string,
): boolean {
  return (
    resource.meta?.tag?.some(
      (t) => t.system === TEMPLATE_CATEGORY_SYSTEM && t.code === category,
    ) ?? false
  );
}

/** True when a QuestionnaireResponse represents provider transfer context (not a session note). */
export function isTransferQuestionnaireResponse(
  qr: QuestionnaireResponse,
  questionnaire?: Questionnaire,
): boolean {
  if (hasTemplateCategoryTag(qr, TRANSFER_TEMPLATE_CATEGORY)) return true;
  if (questionnaire && hasTemplateCategoryTag(questionnaire, TRANSFER_TEMPLATE_CATEGORY)) {
    return true;
  }
  const title = (questionnaire?.title ?? questionnaire?.name ?? '').toLowerCase();
  return title.includes('transfer');
}

// ---------------------------------------------------------------------------
// Transfer QR item linkIds
// ---------------------------------------------------------------------------

export const TRANSFER_QR_LINK_IDS = {
  reasons: 'transfer-reasons',
  explanation: 'transfer-explanation',
  recommendedClinicians: 'recommended-clinicians',
} as const;

// ---------------------------------------------------------------------------
// Session format preference values (PRD)
// ---------------------------------------------------------------------------

export const SESSION_FORMAT_VALUES = [
  'in-person',
  'virtual',
  'hybrid',
  'no-preference',
] as const;

export type SessionFormatValue = (typeof SESSION_FORMAT_VALUES)[number];

export const SESSION_FORMAT_OPTIONS: { value: SessionFormatValue; label: string }[] = [
  { value: 'in-person', label: 'In-Person' },
  { value: 'virtual', label: 'Virtual' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'no-preference', label: 'No Preference' },
];
